import { DatePicker } from "antd";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { Line } from "react-chartjs-2";
import { useDispatch, useSelector } from "react-redux";
import CardBlock from "../../common/components/Card";
import DataTable from "../../common/components/DataTable/WhiteHeaderTable";
import EditableFormInput from "../../common/components/EditableText/EditableFormInput";
import { getClinicAnalytics, getOnlyProviders, setGlobalLoading } from "../../store/actions";
const dateFormat = "MM/DD/YYYY";

const ProcedureAnalytics = () => {
  const dispatch = useDispatch();
  const [startDate, setStartDate] = useState();
  const [endDate, setEndDate] = useState();
  const [procedureData, setProcedureData] = useState([]);
  const [referralsProvider, setReferralsProvider] = useState([]);
  const [tableData] = useState([]);
  const [data, setData] = useState({
    labels: [],
    datasets: [],
  });
  const [selectedProvider, setSelectedProvider] = useState(0);

  const clinic_analytics = useSelector((state) => state.common.clinic_analytics);
  const only_providers = useSelector((state) => state.common.only_providers);

  if (tableData?.length < minRowCount + 1) {
    emptyRows = new Array(minRowCount - tableData?.length).fill({ label: "" });
  }

  const fetchApi = async () => {
    dispatch(setGlobalLoading(true));
    await dispatch(getOnlyProviders());
    dispatch(setGlobalLoading(false));
  };

  useEffect(() => {
    if (!only_providers || only_providers.length == 0) {
      fetchApi();
    }
  }, [only_providers]);

  const getMillionsValue = (value) => {
    let newValue = value;
    if (value >= 1000) {
      const suffixes = ["", "K", "M", "B", "T"];
      const suffixNum = Math.floor(("" + value).length / 3);
      let shortValue = "";
      for (let precision = 2; precision >= 1; precision--) {
        shortValue = parseFloat((suffixNum != 0 ? value / Math.pow(1000, suffixNum) : value).toPrecision(precision));
        const dotLessShortValue = (shortValue + "").replace(/[^a-zA-Z 0-9]+/g, "");
        if (dotLessShortValue.length <= 2) {
          break;
        }
      }
      if (shortValue % 1 != 0) shortValue = shortValue.toFixed(1);
      newValue = shortValue + suffixes[suffixNum];
    }
    return newValue;
  };

  const chartOptions = {
    responsive: true,
    animation: false,
    maintainAspectRatio: false,
    defaults: {
      elements: {
        point: {
          backgroundColor: "#000000",
          borderColor: "#000000",
        },
      },
    },
    layout: {
      padding: 30,
    },
    plugins: {
      legend: {
        position: "right",
        display: false,
        labels: {
          color: "#152935",
          family: '"IBM plex sans", "Helvetica Neue", sans-serif',
        },
      },
    },
    scales: {
      y: {
        min: 0,
        stacked: true,
        ticks: {
          font: {
            size: "0.875rem",
            family: '"IBM plex sans", "Helvetica Neue", sans-serif',
          },
          color: "#152935",
          callback: function (value, index, values) {
            return getMillionsValue(value);
          },
        },
      },
      x: {
        ticks: {
          padding: 10,
          font: {
            size: "0.875rem",
            family: '"IBM plex sans", "Helvetica Neue", sans-serif',
          },
          color: "#000",
        },
      },
    },
  };

  const fetchEvents = async () => {
    dispatch(setGlobalLoading(true));
    await dispatch(getClinicAnalytics(startDate, endDate, selectedProvider));
    dispatch(setGlobalLoading(false));
  };

  useEffect(() => {
    fetchEvents();
  }, [startDate, endDate]);

  useEffect(() => {
    if (!clinic_analytics) return;
    const {
      labels,
      referrals_provider,
      clinic_confirmed_but_no_show_data,
      clinic_not_confirmed_data,
      clinic_checked_in_data,
      clinic_finalized_data,
    } = clinic_analytics;

    setReferralsProvider(referrals_provider);

    setProcedureData([
      {
        procedure: "Total clinic appointments that are finalized",
        total: labels?.clinic_finalized_label,
      },
      {
        procedure: "Total clinic appointments that are checked-in",
        total: labels?.clinic_checked_in_label,
      },
      {
        procedure: "Total clinic appointments that are confirmed but no show",
        total: labels?.clinic_confirmed_but_no_show_label,
      },
      {
        procedure: "Total clinic appointments that are not confirmed",
        total: labels?.clinic_not_confirmed_label,
      },
    ]);

    const newData = {};
    newData.labels = clinic_analytics?.clinic_finalized_data?.map((procedure) => {
      return moment(procedure.procedure_date, "MM-YYYY").format("MMM-YY");
    });

    newData.datasets = [
      {
        label: "Clinic Finalized",
        data: clinic_analytics?.clinic_finalized_data?.map((procedure) => {
          return procedure?.total;
        }),
        fill: true,
        backgroundColor: "rgba(25,128,56,0.75)",
        borderColor: "rgba(25,128,56,1)",
        pointBackgroundColor: "#000000",
      },
      {
        label: "Clinic Checked in",
        data: clinic_analytics?.clinic_checked_in_data?.map((procedure) => {
          return procedure?.total;
        }),
        fill: true,
        backgroundColor: "rgba(130,207,255,0.75)",
        borderColor: "rgba(130,207,255,1)",
        pointBackgroundColor: "#000000",
      },
      {
        label: "Clinic Confirmed but No Show",
        data: clinic_confirmed_but_no_show_data?.map((procedure) => {
          return procedure?.total;
        }),
        fill: true,
        backgroundColor: "rgba(255,231,163,0.75)",
        borderColor: "rgba(255,231,163,1)",
        pointBackgroundColor: "#000000",
      },
      {
        label: "Clinic Checked in 2",
        data: clinic_not_confirmed_data?.map((procedure) => {
          return procedure?.total;
        }),
        fill: true,
        backgroundColor: "rgba(255,175,210,0.75)",
        borderColor: "rgba(255,175,210,1)",
        pointBackgroundColor: "#000000",
      },
    ];

    setData(newData);
  }, [clinic_analytics]);

  const minRowCount = 8;
  let emptyRows = [];
  if (tableData?.length < minRowCount + 1) {
    emptyRows = new Array(minRowCount - tableData?.length).fill({ label: "" });
  }

  const changeProvider = (value) => {
    setSelectedProvider(value);
    dispatch(getClinicAnalytics(startDate, endDate, value));
  };

  return (
    <React.Fragment>
      <div className="cardBlock" style={{ height: "calc(65% - 20px)", width: "100%" }}>
        <div className="filterContainer" style={{ padding: 0, paddingRight: "35px", paddingLeft: "35px" }}>
          <div className="cardBlock__title">Procedural Volume</div>
          <div className="statusFilter" style={{ minWidth: "200px" }}>
            <EditableFormInput
              label={"Provider"}
              value={selectedProvider}
              handleChange={changeProvider}
              fullWidth={true}
              type={"check"}
              tabIndex={3}
              optionKey={"id"}
              valueKey={"value"}
              options={only_providers || []}
            />
          </div>
          <div className="statusFilter" style={{ minWidth: "200px" }}>
            <DatePicker
              format={dateFormat}
              placeholder="Start Date"
              value={startDate}
              onChange={(val) => {
                setStartDate(val);
              }}
            />
          </div>
          <div className="statusFilter" style={{ minWidth: "200px" }}>
            <DatePicker
              format={dateFormat}
              placeholder="End Date"
              value={endDate}
              onChange={(val) => {
                setEndDate(val);
              }}
            />
          </div>
        </div>
        <div style={{ height: "90%" }}>
          <Line data={data} options={chartOptions} height={75} />
        </div>
      </div>
      <div
        className="cardBlock"
        style={{
          width: "100%",
          height: "calc(35% - 10px)",
          display: "flex",
        }}
      >
        <CardBlock title="Clinic Breakdown" varient="one_third" other="" contentPadding="1rem">
          <DataTable
            title="procedure-breakdown"
            labels={["Procedures", "Total"]}
            widths={["70%", "30%"]}
            tableWidth={300}
            defaultActive={-1}
            sizeHeightFix="100%"
            isFixHeight
            isNormal
            bordered
            disableIcon
            disableDelete
            columns={[
              { sortable: false, key: "procedure" },
              { sortable: false, key: "total" },
            ]}
            rowData={procedureData?.map((item, index) => {
              return {
                procedure: <b>{item.procedure}</b>,
                total: item.total,
              };
            })}
            emptyRows={emptyRows}
            sorts={["", ""]}
          />
        </CardBlock>
        <CardBlock title="Provider Clinic Volume" varient="one_third" other="" contentPadding="1rem">
          <DataTable
            title="provider-hospital-volume"
            labels={["Provider", "Total"]}
            widths={["70%", "30%"]}
            tableWidth={300}
            defaultActive={-1}
            sizeHeightFix="100%"
            isFixHeight
            isNormal
            bordered
            disableIcon
            disableDelete
            columns={[
              { sortable: false, key: "value" },
              { sortable: false, key: "total" },
            ]}
            rowData={referralsProvider.map((provider, index) => {
              return {
                id: index,
                value: provider?.value,
                total: provider.total,
              };
            })}
            emptyRows={emptyRows}
            sorts={["", ""]}
          />
        </CardBlock>
      </div>
    </React.Fragment>
  );
};

export default ProcedureAnalytics;
